import type { components } from "@brandex/api-client";
import { translate, type MessageKey } from "@brandex/i18n";
import { useBrandex } from "@brandex/dashboard";
import { Alert, Badge, Button, Field, Spinner, Textarea, cn } from "@brandex/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, ShieldCheck, ShieldX } from "lucide-react";
import { useState } from "react";

import { QueryError, fieldError } from "../views";

type Row = components["schemas"]["VerificationRowDto"];
type Status = components["schemas"]["VerificationStatusDto"];

/** The BMDC online register where staff look numbers up. */
export const BMDC_REGISTER = "https://verify.bmdc.org.bd/";

const TABS: { status: Status | "all"; label: string }[] = [
  { status: "pending", label: "Waiting" },
  { status: "rejected", label: "Rejected" },
  { status: "verified", label: "Verified" },
  { status: "not_submitted", label: "No number" },
  { status: "all", label: "All" },
];

const TONE: Record<Status, "success" | "warning" | "neutral" | "accent"> = {
  pending: "accent",
  verified: "success",
  rejected: "warning",
  not_submitted: "neutral",
};

/**
 * Registration verification (ADR 0016 §4): Compliance checks each number on
 * the BMDC register and records the result. A doctor's website goes live
 * only once verified. Every check is audited and kept as history.
 */
export function VerificationPage() {
  const { api } = useBrandex();
  const [tab, setTab] = useState<Status | "all">("pending");
  const key = ["admin", "verifications", tab];
  const list = useQuery<Row[]>({
    queryKey: key,
    queryFn: async () => {
      const { data } = await api.http.GET("/v1/admin/verifications", {
        params: { query: tab === "all" ? { all: true } : { status: tab } },
      });
      if (data === undefined) throw new Error("Empty response");
      return data;
    },
  });
  return (
    <div className="grid gap-6">
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight">Registration checks</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Check each number on the{" "}
          <a href={BMDC_REGISTER} target="_blank" rel="noopener noreferrer" className="font-medium text-azure-400 hover:underline">BMDC online register</a>
          , then record the result. A doctor&apos;s website goes live only after their registration is verified. Rejections need a reason, which the doctor sees.
        </p>
      </header>
      <div role="tablist" aria-label="Status" className="flex flex-wrap gap-1 rounded-[var(--radius-pill)] border border-border p-1 sm:w-fit">
        {TABS.map((x) => (
          <button
            key={x.status}
            type="button"
            role="tab"
            aria-selected={tab === x.status}
            onClick={() => setTab(x.status)}
            className={cn("rounded-full px-3 py-1 text-sm text-muted-foreground hover:text-foreground", tab === x.status && "bg-primary text-primary-foreground hover:text-primary-foreground")}
          >
            {x.label}
          </button>
        ))}
      </div>
      {list.isPending ? <Spinner label="Loading…" /> : null}
      {list.isError ? <QueryError error={list.error} /> : null}
      {list.data?.length === 0 ? <p className="text-sm text-muted-foreground">Nothing here.</p> : null}
      <ul className="grid gap-4">
        {list.data?.map((r) => <Doctor key={r.organization_id} r={r} />)}
      </ul>
    </div>
  );
}

function Doctor({ r }: { r: Row }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const decide = useMutation({
    mutationFn: async (outcome: "verified" | "rejected") => {
      const { data } = await api.http.POST("/v1/admin/orgs/{org_id}/verification", {
        params: { path: { org_id: r.organization_id } },
        body: { outcome, note: note.trim() },
      });
      return data;
    },
    onSuccess: () => {
      setNote("");
      void qc.invalidateQueries({ queryKey: ["admin", "verifications"] });
    },
  });
  const canCheck = r.registration_no != null && r.registration_no !== "";
  return (
    <li className="glass grid gap-4 rounded-[var(--radius-lg)] p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-lg font-semibold">{r.doctor_name}</p>
          <p className="text-sm text-muted-foreground">{translate("en", `specialty.${r.specialty}` as MessageKey)} · {r.designation}</p>
        </div>
        <Badge tone={TONE[r.status]}>{r.status.replace("_", " ")}</Badge>
      </div>
      <dl className="grid gap-2 text-sm sm:grid-cols-3">
        <div><dt className="text-muted-foreground">Registration no.</dt><dd className="font-mono font-semibold">{r.registration_no ?? "—"}</dd></div>
        <div><dt className="text-muted-foreground">Submitted</dt><dd>{r.submitted_at ? new Date(r.submitted_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Dhaka" }) : "—"}</dd></div>
        {r.reason ? <div><dt className="text-muted-foreground">Reason given</dt><dd>{r.reason}</dd></div> : null}
      </dl>
      {canCheck ? (
        <div className="grid gap-3 border-t border-border pt-4">
          <Field label="Note" hint="What you checked. Required to reject; the doctor sees a rejection reason." error={fieldError(decide.error, "note")}>
            <Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          {decide.isError && fieldError(decide.error, "note") === undefined ? <QueryError error={decide.error} /> : null}
          <div className="flex flex-wrap gap-2">
            <a href={BMDC_REGISTER} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium hover:bg-muted">
              <ExternalLink className="h-4 w-4" aria-hidden />
              Open BMDC register
            </a>
            <Button size="sm" loading={decide.isPending && decide.variables === "verified"} onClick={() => decide.mutate("verified")}>
              <ShieldCheck className="h-4 w-4" aria-hidden />
              Mark verified
            </Button>
            <Button size="sm" variant="outline" loading={decide.isPending && decide.variables === "rejected"} disabled={note.trim() === ""} onClick={() => decide.mutate("rejected")}>
              <ShieldX className="h-4 w-4" aria-hidden />
              Reject
            </Button>
          </div>
        </div>
      ) : (
        <Alert tone="info">No registration number yet. The doctor is asked to add one in their portal.</Alert>
      )}
      {r.history.length > 0 ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">History ({r.history.length})</summary>
          <ol className="mt-2 grid gap-1.5">
            {r.history.map((h, i) => (
              <li key={i} className="flex flex-wrap gap-2">
                <Badge tone={TONE[h.outcome]}>{h.outcome}</Badge>
                <span className="font-mono">{h.registration_no}</span>
                <span className="text-muted-foreground">{new Date(h.checked_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Dhaka" })}</span>
                {h.note ? <span>“{h.note}”</span> : null}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </li>
  );
}
